import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BannerService from "services/BannerService";

// Initial state for the banners slice
const initialState = {
  loading: false,
  banners: [],
  selectedBanner: null,
  successMessage: "",
  errorMessage: "",
  messageKey: "",
  showMessage: false, // Add this flag
};

// Async thunk to fetch banners
export const getBanners = createAsyncThunk(
  "banners/getBanners",
  async ({ banner_id, languageId }, { rejectWithValue }) => {
    try {
      const response = await BannerService.getBanner({ banner_id, languageId });
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "banner.fetch_error",
      });
    }
  }
);

// Async thunk to fetch a single banner
export const getBannerById = createAsyncThunk(
  "banners/getBannerById",
  async (bannerId, { rejectWithValue }) => {
    try {
      const response = await BannerService.getBannerById(bannerId);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "banner.fetch_error",
      });
    }
  }
);

// Async thunk to create banners
export const postBanners = createAsyncThunk(
  "banners/postBanners",
  async ({ data, headers }, { rejectWithValue }) => {
    try {
      const response = await BannerService.createBanner({
        params: data,
        headers: headers,
      });
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "banner.create_error",
      });
    }
  }
);

// Async thunk to update banners
export const updateBanners = createAsyncThunk(
  "banners/updateBanners",
  async ({ data, bannerId }, { rejectWithValue }) => {
    try {
      const response = await BannerService.updateBanner({
        params: data,
        bannerId,
      });
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "banner.update_error",
      });
    }
  }
);

// Async thunk to delete a banner
export const deleteBanner = createAsyncThunk(
  "banners/deleteBanner",
  async (bannerId, { rejectWithValue, dispatch }) => {
    try {
      await BannerService.deleteBanner(bannerId);
      dispatch(getBanners()); // Refresh the banners list after deletion
      return {
        bannerId,
        messageKey: "banner.delete_success",
      };
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error deleting banner",
        messageKey: "banner.delete_error",
      });
    }
  }
);

// Create the slice for banners
const bannerSlice = createSlice({
  name: "banners",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
      state.messageKey = "";
      state.showMessage = false; // Clear show flag
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBanners.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBanners.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.banners = payload;
        state.showMessage = false; // Clear message on successful fetch
      })
      .addCase(getBanners.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload?.message || payload;
        state.messageKey = payload?.messageKey || "banner.fetch_error";
        state.showMessage = true; // Show error message
      })
      .addCase(getBannerById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBannerById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedBanner = payload;
      })
      .addCase(getBannerById.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload?.message || payload;
        state.messageKey = payload?.messageKey || "banner.fetch_error";
      })
      .addCase(postBanners.pending, (state) => {
        state.loading = true;
        state.successMessage = "";
        state.errorMessage = "";
        state.messageKey = "";
      })
      .addCase(postBanners.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "success"; // Add this instead of empty string
        state.messageKey = "banner.create_success";
      })
      .addCase(postBanners.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload?.message || payload;
        state.messageKey = payload?.messageKey || "banner.create_error";
      })
      .addCase(updateBanners.pending, (state) => {
        state.loading = true;
        state.successMessage = "";
        state.errorMessage = "";
        state.messageKey = "";
      })
      .addCase(updateBanners.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "success"; // Add this instead of empty string
        state.messageKey = "banner.update_success";
      })
      .addCase(updateBanners.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload?.message || payload;
        state.messageKey = payload?.messageKey || "banner.update_error";
      })
      .addCase(deleteBanner.fulfilled, (state, { payload }) => {
        // Remove the deleted banner from the banners list
        state.banners = state.banners.filter(
          (banner) => banner.banner_image_id !== payload.bannerId
        );
        state.successMessage = "success";
        state.messageKey = payload.messageKey;
        state.showMessage = true; // Show success message
      })
      .addCase(deleteBanner.rejected, (state, { payload }) => {
        state.errorMessage = payload?.message || payload;
        state.messageKey = payload?.messageKey || "banner.delete_error";
        state.showMessage = true; // Show error message
      });
  },
});

// Export the actions and reducer
export const { clearMessage, setSuccessMessage, setErrorMessage } =
  bannerSlice.actions;
export default bannerSlice.reducer;
